import {
  forwardRef,
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  useContext,
  useEffect,
  MouseEvent,
  useRef,
  useCallback,
  KeyboardEvent as ReactKeyboardEvent
} from 'react';

import Progress from '../Progress';
import { getFocusables } from '../../utils';
import Backdrop from '../Backdrop';
import Flex from '../Flex';
import Button from '../Button';
import { ForwardProps } from '../../types';
import {
  useAfterInitialEffect,
  useConsolidatedRef,
  useI18n,
  useOuterEvent,
  useUID
} from '../../hooks';
import Icon, { registerIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as minusIcon from '../Icon/icons/minus.icon';
import * as dockIcon from '../Icon/icons/dock.icon';
import * as undockIcon from '../Icon/icons/undock.icon';
import Text from '../Text';
import { Count } from '../Badges';

import { ModalManagerContext } from './Contexts';
import MinimizedModal from './MinimizedModal';
import type { ModalProps } from './Modal.types';
import {
  StyledModal,
  StyledModalHeader,
  StyledModalContent,
  StyledModalActions,
  StyledModalChildrenWrap
} from './Modal.styles';

registerIcon(timesIcon, minusIcon, dockIcon, undockIcon);

const Modal: FunctionComponent<ModalProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<ModalProps>, refArg: Ref<HTMLElement>) => {
    const {
      as,
      progress = false,
      heading,
      count,
      children,
      actions,
      stretch = false,
      center = false,
      defaultFocus,
      onBeforeOpen,
      onAfterOpen,
      onBeforeClose,
      onAfterClose,
      onRequestDismiss,
      onRequestMinimize,
      onRequestMaximize,
      onRequestDock,
      onRequestActivate,
      ...restProps
    } = props;

    const { ModalContext } = useContext(ModalManagerContext);
    const {
      alert,
      dismissible,
      minimizable,
      maximizable,
      dockable,
      state,
      dismiss,
      minimize,
      maximize,
      dock,
      unmount,
      top
    } = useContext(ModalContext);

    const modalRef = useConsolidatedRef(refArg);
    const modalContentRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const id = useUID();
    const t = useI18n();

    const tryDismiss = () => {
      if (onRequestDismiss?.() !== false) {
        dismiss();
      }
    };

    const setFocus = () => {
      const focusables = getFocusables(modalRef);

      if (defaultFocus?.current) {
        defaultFocus.current.focus();
      } else {
        const focusable =
          focusables.find(el => {
            return modalContentRef.current?.contains(el);
          }) ??
          closeButtonRef.current ??
          focusables[0];

        focusable?.focus();
      }
    };

    const onKeyDown = useCallback((e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const focusables = getFocusables(modalRef);
        if (!focusables) return;
        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];
        if (
          document.activeElement === modalRef.current ||
          (document.activeElement === firstFocusable && e.shiftKey)
        ) {
          lastFocusable?.focus();
          e.preventDefault();
        } else if (
          (document.activeElement === lastFocusable && !e.shiftKey) ||
          focusables.length < 2
        ) {
          firstFocusable?.focus();
          e.preventDefault();
        }
      }
    }, []);

    useEffect(() => {
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [onKeyDown]);

    useOuterEvent('mousedown', [modalRef], e => {
      if (['minimized', 'docked', 'closed'].includes(state)) return;

      if (dismissible) {
        if (e.target === backdropRef.current) {
          tryDismiss();
        }
      } else {
        modalRef.current?.focus();
      }
    });

    useEffect(() => {
      if (state === 'minimized') return;
      if (state === 'open' || state === 'maximized' || state === 'docked') setFocus();
    }, [state]);

    // Dismiss on Escape needs to be handled using React event handling to be cancelable using SyntheticEvent.stopPropagation.
    const onModalKeyDown = useCallback(
      (e: ReactKeyboardEvent) => {
        if (
          dismissible &&
          (state === 'open' || state === 'maximized') &&
          e.key === 'Escape' &&
          top
        ) {
          tryDismiss();
        }
      },
      [dismissible, state, top, tryDismiss]
    );

    const onHeaderActionKeyDown = useCallback((e: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (e.repeat && (e.key === 'Enter' || e.key === ' ')) e.preventDefault();
    }, []);

    // Set a ref based on the initial progress state to be used later to check for setting focus.
    const focusOnProgressChange = useRef(!!progress);
    useAfterInitialEffect(() => {
      // If the progress prop changed and we have already set focus we are done.
      if (!progress && focusOnProgressChange.current) {
        setFocus();
        focusOnProgressChange.current = false;
      }
    }, [progress]);

    const maximized = stretch || state === 'maximized';

    const modal = (
      <Flex
        {...restProps}
        container={{ direction: 'column' }}
        ref={modalRef}
        as={StyledModal}
        forwardedAs={as}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        onKeyDown={onModalKeyDown}
        alert={alert}
        role={alert ? 'alertdialog' : 'dialog'}
        stretch={maximized}
        center={center}
        aria-labelledby={`${id}-heading`}
        aria-modal={state === 'docked' ? 'false' : 'true'}
        state={state}
        // FOR PR REVIEW: This may be too much, this may be fine...
        aria-live='polite'
        aria-busy={progress ? 'true' : 'false'}
      >
        <Flex
          container={{ justify: 'between', alignItems: 'center', gap: 1 }}
          item={{ shrink: 0 }}
          as={StyledModalHeader}
          hasAction={minimizable || dismissible}
        >
          <Flex
            container={{ alignItems: 'center', gap: 1 }}
            as={Text}
            variant='h2'
            id={`${id}-heading`}
          >
            <span>{heading}</span>
            {typeof count === 'number' && <Count>{count}</Count>}
          </Flex>

          <Flex container>
            {maximizable && !alert && state !== 'maximized' && (
              <Button
                icon
                variant='simple'
                aria-label={t('modal_maximize')}
                onKeyDown={onHeaderActionKeyDown}
                onClick={() => {
                  if (onRequestMaximize?.() !== false) {
                    maximize();
                  }
                }}
              >
                <Icon name='undock' />
              </Button>
            )}
            {dockable && !alert && state !== 'docked' && (
              <Button
                icon
                variant='simple'
                aria-label={t('modal_dock')}
                onKeyDown={onHeaderActionKeyDown}
                onClick={() => {
                  if (onRequestDock?.() !== false) {
                    dock();
                  }
                }}
              >
                <Icon name='dock' />
              </Button>
            )}
            {minimizable && !alert && (
              <Button
                icon
                variant='simple'
                aria-label={t('modal_minimize')}
                onKeyDown={onHeaderActionKeyDown}
                onClick={() => {
                  if (onRequestMinimize?.() !== false) {
                    minimize();
                  }
                }}
              >
                <Icon name='minus' />
              </Button>
            )}
            {dismissible && !alert && (
              <Button
                icon
                variant='simple'
                aria-label={t('modal_close')}
                onKeyDown={onHeaderActionKeyDown}
                onClick={() => {
                  tryDismiss();
                  if (dockable) {
                    onBeforeClose?.();
                    unmount();
                    onAfterClose?.();
                  }
                }}
                ref={closeButtonRef}
              >
                <Icon name='times' />
              </Button>
            )}
          </Flex>
        </Flex>

        <Flex item={{ grow: 1 }} as={StyledModalContent} ref={modalContentRef}>
          {children && <StyledModalChildrenWrap>{children}</StyledModalChildrenWrap>}

          <Progress
            visible={!!progress}
            placement='local'
            message={typeof progress === 'object' ? progress.message : t('loading')}
          />
        </Flex>

        {actions && (
          <Flex container={{ justify: 'between' }} item={{ shrink: 0 }} as={StyledModalActions}>
            {actions}
          </Flex>
        )}
      </Flex>
    );

    const renderModal =
      state === 'docked' ? (
        modal
      ) : (
        <Backdrop
          ref={backdropRef}
          container={{
            alignItems: center || maximized ? 'center' : 'start'
          }}
          transitionSpeed='slow'
          open={state === 'open' || state === 'maximized'}
          onBeforeTransitionIn={onBeforeOpen}
          onAfterTransitionIn={onAfterOpen}
          onBeforeTransitionOut={onBeforeClose}
          onAfterTransitionOut={() => {
            unmount();
            onAfterClose?.();
          }}
        >
          {modal}
        </Backdrop>
      );

    return state === 'minimized' ? <MinimizedModal {...props} {...restProps} /> : renderModal;
  }
);

export default Modal;
